import chroma from 'chroma-js';
import React from 'react';
import styled from 'styled-components';

import { theme } from '../../theme';
import { body02Styles } from '../typography/Body02';

export interface ChipProps {
    color?: string;
    selected?: boolean;
    className?: string;
    disabled?: boolean;
    value?: string;
    onClick: (event: React.MouseEvent) => void;
    withCheckmark?: boolean;
}

const Chip: React.FC<ChipProps> = ({
    className = '',
    color = theme.semanticColor.primary,
    disabled = false,
    onClick,
    selected,
    value = '',
    withCheckmark = true
}): React.ReactElement => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) {
            return;
        }

        onClick(event);
    };

    return (
        <Root color={ color }
            className={ className }
            disabled={ disabled }
            onClick={ handleClick }
            selected={ selected }
            withCheckmark={ withCheckmark }
        >
            <Label color={ color }
                disabled={ disabled }
                selected={ selected }
            >
                { value }
            </Label>
        </Root>
    );
};

export default Chip;

type GetBackgroundArgs = {
    color?: string;
    disabled?: boolean;
    selected?: boolean;
}

const getBackground = (props: GetBackgroundArgs) => {
    const {
        color,
        disabled,
        selected
    } = props;

    if (disabled) {
        return selected ? theme.color.grey.grey_10 : 'transparent';
    }
    else {
        return selected ? color : 'transparent';
    }
};

export const Root = styled.button<{ color: string; disabled?: boolean; selected?: boolean; withCheckmark?: boolean }>`
    align-items: center;
    background-color: ${ getBackground };
    border-radius: ${ ({ theme }) => theme.size.size_40 };
    border: 1px solid ${ ({ disabled, color }) => (disabled ? theme.color.grey.grey_10 : color) };
    cursor: ${ ({ disabled }) => (disabled ? 'not-allowed' : 'pointer') };
    display: flex;
    height: ${ ({ theme }) => theme.size.size_40 };
    padding: 0 ${ ({ theme }) => theme.size.size_15 } 0 ${ ({ theme }) => theme.size.size_15 };

    @media (hover: hover) {
        &:hover {
            background-color: ${ ({ color, disabled, selected }) =>
        `${
            !disabled && (selected ? chroma(color).alpha(0.8) : chroma(color).alpha(0.2))
        }` };
        }
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: ${ ({ theme }) => theme.shadow.shadow_i0 };
    }
`;

const Label = styled.span<{ disabled?: boolean; selected?: boolean }>`
    ${ body02Styles }

    color: ${ ({ color, disabled, selected, theme }) =>
        (!disabled ? (selected ? theme.color.white : color)
            : theme.semanticColor.textDisabled
        ) 
};
`;
